/**
 * @fileoverview Hook for managing plan assignment business logic
 * Provides functionality to assign production plans to orders
 */

import { useState, useCallback } from 'react';
import { pb } from '../helpers/pocketbase_helper';
import { 
  TareaOrden, 
  CreateTareaOrdenData,
  UpdateTareaOrdenData,
  TAREA_ORDEN_COLLECTION 
} from '../types/collections/TareaOrden';
import { 
  LogPlanCentroCosto,
  CreateLogPlanCentroCostoData,
  LOG_PLAN_CENTRO_COSTO_COLLECTION 
} from '../types/collections/LogPlanCentroCosto';
import { OrderDataAnalysisService } from '../services/OrderDataAnalysisService';
import { ProcessMappingService } from '../services/ProcessMappingService';
import { redistributePercentages } from '../utils/percentageDistribution';

/**
 * Interface for order data analysis result
 */
interface OrderDataAnalysisResult {
  orderId: string;
  hasInternalData: boolean;
  internalRelationships: any[];
  requiredProcesses: string[];
}

/**
 * Data structure for plan assignment request
 */
export interface AsignacionPlanData {
  ordenId: string;
  planId: string;
  usuarioId: string;
}

/**
 * Response structure for plan assignment
 */
export interface AsignacionPlanResponse {
  success: boolean;
  data?: {
    tareaOrden: TareaOrden | null;
    logsPlan: LogPlanCentroCosto[];
  };
  error?: string;
}

/**
 * Hook for managing plan assignment operations
 * @returns Object with state and functions for plan assignment
 */
export const useAsignacionPlan = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [tareaOrden, setTareaOrden] = useState<TareaOrden | null>(null);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError("");
  }, []);

  /**
   * Set loading state
   */
  const setLoadingState = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  /**
   * Set error state
   */
  const setErrorState = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, []);

  /**
   * Validate preconditions for plan assignment
   */
  const validatePreconditions = useCallback(async (data: AsignacionPlanData): Promise<boolean> => {
    try {
      // Validate input data
      if (!data.ordenId || data.ordenId.trim() === '') {
        setError("ID de orden es requerido");
        return false;
      }

      if (!data.planId || data.planId.trim() === '') {
        setError("ID de plan es requerido");
        return false;
      }

      // Validate order exists and is active
      const orden = await pb.collection('orden_produccion').getOne(data.ordenId);
      if (!orden || !orden.activo) {
        setError("La orden no existe o no está activa");
        return false;
      }

      // Check if order already has a plan assigned
      if (orden.tarea) {
        setError("La orden ya tiene un plan asignado");
        return false;
      }

      // Validate plan exists and is active
      const plan = await pb.collection('plan_centro_costo').getOne(data.planId);
      if (!plan || !plan.activo) {
        setError("El plan no existe o no está activo");
        return false;
      }

      return true;
    } catch (err: any) {
      console.error("Error validating preconditions:", err);
      
      // Provide more specific error messages based on the error type
      if (err?.status === 404) {
        if (err?.data?.code === 'record_not_found') {
          setError("No se encontró la orden o el plan especificado");
        } else {
          setError("Recurso no encontrado");
        }
      } else if (err?.status === 400) {
        setError("Datos de entrada inválidos");
      } else if (err?.status === 403) {
        setError("No tiene permisos para realizar esta acción");
      } else if (err?.message) {
        setError(`Error de validación: ${err.message}`);
      } else {
        setError("Error al validar precondiciones");
      }
      
      return false;
    }
  }, []);

  /**
   * Create log entries for each cost center in the plan
   */
  const createPlanLogs = useCallback(async (
    planId: string, 
    ordenId: string
  ): Promise<LogPlanCentroCosto[]> => {
    try {
      // 1. Obtener los procesos requeridos por la orden
      const orderData = await OrderDataAnalysisService.analyzeOrderData(ordenId) as OrderDataAnalysisResult;
      const requiredProcesses = await ProcessMappingService.getRequiredProcesses(orderData.internalRelationships);
      
      // 2. Obtener el plan con los datos expandidos
      const plan = await pb.collection('plan_centro_costo').getOne(planId, {
        expand: 'centro_costo_peso.centro_costo.proceso,centro_costo_peso.centro_costo.operario,centro_costo_peso.centro_costo.proceso_proveedor'
      });

      if (!plan || !plan.centro_costo_peso || plan.centro_costo_peso.length === 0) {
        throw new Error("Plan no válido o sin centros de costo");
      }

      const logs: LogPlanCentroCosto[] = [];
      
      // 3. Prepare centro_costo_peso data for redistribution
      const centroCostoPesoArray = [];
      const usedCentroCostoIds = [];
      
      // First pass: collect all centro_costo_peso data and determine which will be used
      for (let i = 0; i < plan.centro_costo_peso.length; i++) {
        const centroCostoPesoId = plan.centro_costo_peso[i];
        // Obtener el ID del centro_costo desde el centro_costo_peso
        let centroCostoId = '';
        let centroCostoNombre = '';
        let centroCostoOperario = '';
        let peso = 0;
        
        // Intentar obtener desde los datos expandidos primero
        if (plan.expand?.centro_costo_peso && plan.expand.centro_costo_peso[i]?.expand?.centro_costo) {
          centroCostoId = plan.expand.centro_costo_peso[i].expand.centro_costo.id;
          centroCostoNombre = plan.expand.centro_costo_peso[i].expand.centro_costo.nombre;
          // Priorizar operario sobre proceso_proveedor
          centroCostoOperario = plan.expand.centro_costo_peso[i].expand.centro_costo.operario || 
                               plan.expand.centro_costo_peso[i].expand.centro_costo.proceso_proveedor;
          peso = plan.expand.centro_costo_peso[i].peso || 0;

        } 
        // Si no hay datos expandidos, obtener directamente
        else if (typeof centroCostoPesoId === 'string') {
          // Intentar obtener el centro_costo_peso para extraer el centro_costo
          try {
            const centroCostoPeso = await pb.collection('centro_costo_peso').getOne(centroCostoPesoId, {
              expand: 'centro_costo.proceso,centro_costo.operario,centro_costo.proceso_proveedor'
            });
            centroCostoId = centroCostoPeso.centro_costo;
            peso = centroCostoPeso.peso || 0;
            if (centroCostoPeso.expand?.centro_costo) {
              centroCostoNombre = centroCostoPeso.expand.centro_costo.nombre;
              // Priorizar operario sobre proceso_proveedor
              centroCostoOperario = centroCostoPeso.expand.centro_costo.operario || 
                                   centroCostoPeso.expand.centro_costo.proceso_proveedor;
            }

          } catch (err) {
            console.error(`Error obteniendo centro_costo_peso ${centroCostoPesoId}:`, err);
            continue; // Saltar este registro si no se puede obtener
          }
        } else {
          // Si es un objeto, intentar extraer el centro_costo
          centroCostoId = centroCostoPesoId.centro_costo || '';
          peso = centroCostoPesoId.peso || 0;
        }

        // 4. Verificar si el proceso del centro de costo está en los procesos requeridos
        let procesoNombre = '';
        if (plan.expand?.centro_costo_peso && plan.expand.centro_costo_peso[i]?.expand?.centro_costo?.expand?.proceso) {
          procesoNombre = plan.expand.centro_costo_peso[i].expand.centro_costo.expand.proceso.nombre;
        } else {
          // Si no hay datos expandidos, obtener el proceso directamente
          try {
            const centroCosto = await pb.collection('centro_costo').getOne(centroCostoId, {
              expand: 'proceso'
            });
            procesoNombre = centroCosto.expand?.proceso?.nombre || '';
          } catch (err) {
            console.error(`Error obteniendo proceso del centro de costo ${centroCostoId}:`, err);
            continue;
          }
        }

        // Determine the source of the assignment (operario or proceso_proveedor)
        const operarioId = plan.expand?.centro_costo_peso && plan.expand.centro_costo_peso[i]?.expand?.centro_costo?.operario;
        const proveedorId = plan.expand?.centro_costo_peso && plan.expand.centro_costo_peso[i]?.expand?.centro_costo?.proceso_proveedor;
        
        // Add to centro_costo_peso array for redistribution
        centroCostoPesoArray.push({
          id: centroCostoPesoId,
          centro_costo: centroCostoId,
          peso: peso,
          centroCostoNombre,
          centroCostoOperario: operarioId || proveedorId, // Use whichever is available
          centroCostoProveedor: proveedorId,
          centroCostoOperarioId: operarioId, // Keep track of the actual operario ID
          procesoNombre
        });

        // Only add to used list if process is required
        if (requiredProcesses.includes(procesoNombre)) {
          usedCentroCostoIds.push(centroCostoId);
        }
      }

      // 5. Redistribute percentages for used tasks
      let redistributedTasks;
      try {
        redistributedTasks = redistributePercentages(centroCostoPesoArray, usedCentroCostoIds);
      } catch (redistributionError) {
        console.error('Error redistributing percentages:', redistributionError);
        throw new Error('Error al redistribuir porcentajes del plan');
      }

      // 5.1. Validar ANTES de crear cualquier log que todos los centros de costo tengan operario o proveedor
      const centrosCostoInvalidos = [];
      for (let i = 0; i < redistributedTasks.length; i++) {
        const task = redistributedTasks[i];
        const hasOperario = task.centroCostoOperarioId && task.centroCostoOperarioId.trim() !== '';
        const hasProveedor = task.centroCostoProveedor && task.centroCostoProveedor.trim() !== '';
        
        if (!hasOperario && !hasProveedor) {
          centrosCostoInvalidos.push(task.centroCostoNombre || 'Sin nombre');
        }
      }

      // Si hay centros de costo sin operario ni proveedor, lanzar error ANTES de crear cualquier log
      if (centrosCostoInvalidos.length > 0) {
        const nombresInvalidos = centrosCostoInvalidos.join(', ');
        throw new Error(
          `Debe asignar un operario o proveedor a los siguientes centros de costo antes de crear el plan: ${nombresInvalidos}`
        );
      }

      // 6. Create logs for redistributed tasks (all validations passed)
      for (let i = 0; i < redistributedTasks.length; i++) {
        const task = redistributedTasks[i];

        // Asignar orden_plan, prioridad y ready status basado en los logs creados
        const ordenPlan = logs.length + 1; // 1, 2, 3... secuencial para mantener orden del plan
        const prioridad = logs.length + 1; // 1, 2, 3... secuencial para logs creados (puede ser cambiado por usuario)
        const ready = logs.length === 0; // Solo la primera tarea creada está ready
        
        // Use the correct field based on what was assigned
        const logData: CreateLogPlanCentroCostoData = {
          orden: ordenId,
          centro_costo: task.centro_costo,
          operario: task.centroCostoOperarioId || null, // Use the actual operario ID if available
          proceso_proveedor: task.centroCostoProveedor || null, // Use the proveedor ID if available
          prioridad: prioridad,
          orden_plan: ordenPlan, // Add plan order sequence
          ready: ready,
          porcentaje: task.peso // Add redistributed percentage
        };

        const log = await pb.collection(LOG_PLAN_CENTRO_COSTO_COLLECTION.name).create(logData);
        logs.push(log as unknown as LogPlanCentroCosto);
      }

      return logs;
    } catch (err: any) {
      console.error("Error creating plan logs:", err);
      // Preserve specific validation errors
      if (err.message && (err.message.includes('Debe asignar un operario') || err.message.includes('Debe asignar un operario o proveedor') || err.message.includes('centros de costo antes de crear el plan'))) {
        throw err;
      }
      // Preserve plan validation errors
      if (err.message && err.message.includes('Plan no válido')) {
        throw err;
      }
      // Generic error for other cases
      throw new Error("Error al crear logs del plan");
    }
  }, []);

  /**
   * Assign a production plan to an order
   */
  const asignarPlan = useCallback(async (data: AsignacionPlanData): Promise<AsignacionPlanResponse> => {
    try {
      setLoading(true);
      setError("");

      // Validate preconditions
      const isValid = await validatePreconditions(data);
      if (!isValid) {
        return { success: false, error: error };
      }

      // Create plan logs first
      const logsPlan = await createPlanLogs(data.planId, data.ordenId);
      
      // Create tarea_orden
      const tareaData: CreateTareaOrdenData = {
        orden_produccion: data.ordenId,
        plan: data.planId,
        log_plan: logsPlan[0]?.id || '', // Reference to first log
        inicio: new Date().toISOString(),
      };

      const nuevaTarea = await pb.collection(TAREA_ORDEN_COLLECTION.name).create(tareaData);
      
      // Update all logs with tarea_orden ID
      for (const log of logsPlan) {
        await pb.collection(LOG_PLAN_CENTRO_COSTO_COLLECTION.name).update(log.id, {
          tarea_orden: nuevaTarea.id
        });
      }
      
      // Update orden_produccion with task reference
      await pb.collection('orden_produccion').update(data.ordenId, {
        tarea: nuevaTarea.id
      });

      setTareaOrden(nuevaTarea as unknown as TareaOrden);
      
      return {
        success: true,
        data: {
          tareaOrden: nuevaTarea as unknown as TareaOrden,
          logsPlan
        }
      };
    } catch (err: any) {
      console.error("Error assigning plan:", err);
      
      // Provide specific error messages
      let errorMessage = "Error al asignar plan";
      if (err?.data?.data?.orden_produccion?.code === "validation_required") {
        errorMessage = "ID de orden es requerido";
      } else if (err?.data?.data?.plan?.code === "validation_required") {
        errorMessage = "ID de plan es requerido";
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [validatePreconditions, createPlanLogs]);

  /**
   * Get task order by order ID
   */
  const getTareaByOrden = useCallback(async (ordenId: string): Promise<TareaOrden | null> => {
    try {
      setLoading(true);
      setError("");

      const orden = await pb.collection('orden_produccion').getOne(ordenId, {
        expand: 'tarea'
      });

      if (orden?.tarea) {
        const tarea = await pb.collection(TAREA_ORDEN_COLLECTION.name).getOne(orden.tarea, {
          expand: 'plan,log_plan'
        });
        
        setTareaOrden(tarea as unknown as TareaOrden);
        return tarea as unknown as TareaOrden;
      }

      return null;
    } catch (err: any) {
      console.error("Error getting task by order:", err);
      setError("Error al obtener tarea de la orden");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update task order
   */
  const updateTareaOrden = useCallback(async (
    id: string, 
    data: UpdateTareaOrdenData
  ): Promise<AsignacionPlanResponse> => {
    try {
      setLoading(true);
      setError("");

      const updatedTarea = await pb.collection(TAREA_ORDEN_COLLECTION.name).update(id, data);
      
      setTareaOrden(updatedTarea as unknown as TareaOrden);
      
      return {
        success: true,
        data: {
          tareaOrden: updatedTarea as unknown as TareaOrden,
          logsPlan: [] // Not returned in updates
        }
      };
    } catch (err: any) {
      console.error("Error updating task order:", err);
      setError("Error al actualizar tarea");
      return { success: false, error: "Error al actualizar tarea" };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Desasignar plan de una orden (solo si no hay avance)
   */
  const desasignarPlan = useCallback(async (ordenId: string): Promise<AsignacionPlanResponse> => {
    try {
      setLoading(true);
      setError("");

      // 1. Obtener la orden con su tarea
      const orden = await pb.collection('orden_produccion').getOne(ordenId, {
        expand: 'tarea'
      });

      if (!orden?.tarea) {
        return { success: false, error: "La orden no tiene un plan asignado" };
      }

      // 2. Obtener la tarea con sus logs
      const tarea = await pb.collection(TAREA_ORDEN_COLLECTION.name).getOne(orden.tarea, {
        expand: 'plan'
      });

      if (!tarea) {
        return { success: false, error: "No se encontró la tarea asignada" };
      }

      // 3. Verificar si hay avance (logs con inicio)
      const logsResult = await pb.collection(LOG_PLAN_CENTRO_COSTO_COLLECTION.name).getList(1, 1000, {
        filter: `orden = "${ordenId}"`,
        requestKey: null
      });

      const logsConAvance = logsResult.items.filter(log => log.inicio && log.inicio !== "");
      
      if (logsConAvance.length > 0) {
        return { 
          success: false, 
          error: "No se puede desasignar el plan porque la orden ya tiene avance. Hay tareas que ya han iniciado." 
        };
      }

      // 4. Eliminar todos los logs del plan
      for (const log of logsResult.items) {
        try {
          await pb.collection(LOG_PLAN_CENTRO_COSTO_COLLECTION.name).delete(log.id);
        } catch (deleteErr) {
          console.warn(`Error eliminando log ${log.id}:`, deleteErr);
        }
      }

      // 5. Eliminar la tarea_orden
      await pb.collection(TAREA_ORDEN_COLLECTION.name).delete(tarea.id);

      // 6. Limpiar la referencia de tarea en la orden
      await pb.collection('orden_produccion').update(ordenId, {
        tarea: null
      });

      setTareaOrden(null);
      
      return {
        success: true,
        data: {
          tareaOrden: null,
          logsPlan: []
        }
      };
    } catch (err: any) {
      console.error("Error desasignando plan:", err);
      
      let errorMessage = "Error al desasignar plan";
      if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    loading,
    error,
    tareaOrden,
    
    // Actions
    asignarPlan,
    desasignarPlan,
    getTareaByOrden,
    updateTareaOrden,
    createPlanLogs,
    
    // Utilities
    clearError,
    setLoading: setLoadingState,
    setError: setErrorState
  };
};
